/**
 * "Soy yo" — fill a party block with the signed-in customer's own company (#81).
 *
 * Paco asked for it on 2026-09-10 (idea 1) and it was greenlit on 2026-09-12 at no cost: a haulier
 * that is itself the Cargador or the Transportista retypes its own Razón Social, NIF and Domicilio
 * on every document, and we already hold all three on its organisation record.
 *
 * ## Where the data comes from, and why not from the organisation endpoint
 *
 * `GET /api/v1/organisations/{orgId}` carries the address but is guarded by `org:read`, which
 * `ORG_USER` — the role that actually fills documents in — does not hold. So the address rides on
 * `CurrentPrincipal.organisation` instead, added to the contract for this (see its description
 * there). That payload is the **credential's own** company, never a company a platform role has
 * selected with `X-Organisation-Id`.
 *
 * That distinction cannot bite today: `DecaCreateScreen` gates the whole create surface on
 * `abilities.writeDecas`, and `abilitiesFor` excludes `SUPER_ADMIN` and `SUPPORT` from
 * `handlesDecas`, so no platform role reaches this form at all. It is still checked below rather
 * than argued, because the day that gate changes this button would quietly start offering
 * ASGTRANS's own details inside a customer's document, and nothing would say so.
 *
 * ## Saving an address the record does not have
 *
 * We told Paco the button would "ask for the address once and remember it". Remembering is
 * `PATCH /api/v1/organisations/{orgId}`, which needs `org:admin`. That route's own docblock says an
 * organisation admin maintains its own company's details, and #81 says not to widen `org:admin` — so
 * a narrow "let any user set the address while it is empty" write would be that widening under
 * another name. The ask therefore appears for a principal that already holds the scope; everyone
 * else gets the name and the NIF filled and is told the record carries no address, and types it into
 * the form exactly as they do today. Nothing is blocked either way.
 *
 * ## Filling, and asking first
 *
 * How a block is written and the question before typed text is replaced live in `partyFill.tsx`,
 * because the address book's suggestions fill blocks too (#159). What stays here is «Soy yo»'s own
 * rule for when to ask: whenever the block holds any text at all.
 */

import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { unwrap } from '@deca/api'

import { api } from '../app/api.ts'
import { useAuth } from '../app/auth.tsx'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { Dialog } from './Dialog.tsx'
import { TextInput } from './form.tsx'
import {
  blockHasText,
  fillColumn,
  useConfirmPartyOverwrite,
  writeParty,
  type PartyColumns,
} from './partyFill.tsx'
import { Button } from './primitives.tsx'

/**
 * The block's columns. Defined in `partyFill.tsx` since the address book fills blocks too (#159),
 * and re-exported here for the callers that have always imported it from «Soy yo».
 */
export type { PartyColumns } from './partyFill.tsx'

interface Company {
  id: string
  legalName: string
  nif?: string
  address?: string | null
}

export function SoyYoAction({
  columns,
  accessibleName,
}: {
  columns: PartyColumns
  /**
   * The button's name for assistive technology, where «Soy yo» alone would be ambiguous: grouping's
   * «Datos comunes» card holds one per party (#137), so each is named for its party, as the labels
   * beside it are. The visible text stays «Soy yo». A section card's header needs none, since the
   * card already says whose block it is.
   */
  accessibleName?: string
}) {
  const { t } = useTranslation()
  const { principal, refreshPrincipal } = useAuth()
  const { organisation: context } = useOrganisationChoice()
  const form = useFormContext()
  // The question before replacing typed text, shared with the address book's suggestions.
  const overwrite = useConfirmPartyOverwrite()

  const [askingAddress, setAskingAddress] = useState(false)
  const [address, setAddress] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [noAddress, setNoAddress] = useState(false)

  const own = principal?.organisation
  // See the class doc: the principal's company is only the right answer while the principal is
  // acting in its own tenancy. A selected context that is some other company means this button has
  // nothing truthful to offer, so it offers nothing.
  const company: Company | null = own && (!context || context.id === own.id) ? own : null
  const canSaveAddress = principal?.scopes.includes('org:admin') ?? false

  if (!company) return null

  /** Name, NIF and — when we have one — address, then decide what to do about a missing address. */
  const apply = () => {
    setNoAddress(false)
    writeParty(form, columns, { name: company.legalName, nif: company.nif, address: company.address })
    // A block with no address column has nothing missing and nothing to ask for.
    if (!columns.address || company.address) return
    if (canSaveAddress) {
      setAddress('')
      setError(null)
      setAskingAddress(true)
    } else {
      // Not a failure and not a dead end: the field is there and typeable, it just will not be
      // remembered by someone who does not maintain the company record.
      setNoAddress(true)
    }
  }

  const click = () => {
    // "Without silently overwriting": a deliberate click may replace what is there, but only after
    // the person is told that is what it does.
    if (blockHasText(form, columns)) overwrite.ask(company.legalName, apply)
    else apply()
  }

  const saveAddress = async () => {
    const value = address.trim()
    if (!value) return
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.PATCH('/api/v1/organisations/{orgId}', {
          params: { path: { orgId: company.id } },
          body: { address: value },
        }),
      )
      // The principal is where this button reads the address from, so it has to be re-read or the
      // next document would ask again for something now on file.
      await refreshPrincipal()
      if (columns.address) fillColumn(form, columns.address, value)
      setAskingAddress(false)
    } catch {
      setError(t('create.soyYo.saveFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button icon="user" onClick={click} aria-label={accessibleName}>
        {t('create.soyYo.action')}
      </Button>

      {noAddress ? <p className="field__hint">{t('create.soyYo.noAddress')}</p> : null}

      {overwrite.dialog}

      <Dialog
        open={askingAddress}
        title={t('create.soyYo.addressTitle')}
        subtitle={t('create.soyYo.addressBody')}
        onClose={() => setAskingAddress(false)}
        busy={busy}
        footer={
          <>
            <Button onClick={() => setAskingAddress(false)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={saveAddress} disabled={busy || !address.trim()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <TextInput
          label={t('create.soyYo.addressLabel')}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          full
          autoFocus
        />
        {error ? <p className="field__error">{error}</p> : null}
      </Dialog>
    </>
  )
}
